import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, KeyRound, Check, Mail, Trash2 } from "lucide-react";
import {
  schoolAnalytics, schoolProgress, poemsProgressCards, schoolStudents, schoolStudent, schoolAddStudents, schoolResetStudent,
  schoolTeachers, onboardAddTeacher, schoolTeacherClasses, schoolResetTeacher, schoolSetTeacherEmail, schoolTeacherEmailCheck, schoolInvites, schoolCancelInvite, schoolInviteCheck, schoolUnassignedClasses, schoolDismissClass, schoolRemoveTeacher, schoolRemoveStudent, schoolSetStudentAge, errorMessage,
} from "./api";
import { card, h2, th, td, field, Bar, Modal, CredentialsModal, useEmailLookup, EmailLookupNote } from "./ui";
import { useSchoolMe, canInvite, inviteBlockedReason } from "./SchoolLicense";
import { TeacherProgressCards, StudentProgressTables } from "./ProgressCards";
import StudentsList from "./StudentsList";
import TeachersList from "./TeachersList";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const classLabel = (c) => `${c.class_name} ${c.section}`;
const parseClasses = (text) => text.split(",").map((x) => x.trim()).filter(Boolean).map((x) => {
  const [class_name, ...rest] = x.split(/\s+/);
  return { class_name, section: rest.join(" ") };
}).filter((c) => c.class_name && c.section);

// Accounts for a submitted roster are created by our team after review, so until then the lists below
// are legitimately empty. Say so, instead of leaving a bare "0".
const ROSTER_TEXT = {
  new: "is waiting for our team to review it",
  reviewed: "has been reviewed — your accounts will be created shortly",
};
function RosterNotice({ me }) {
  if (!me || me.role !== "principal" || me.approval !== "legacy") return null;   // only schools that pre-date licences use the roster form
  if (!me.roster_submitted) {
    return (
      <div className={`${card} bg-gold-500/5 text-sm flex flex-wrap items-center justify-between gap-3`} data-testid="setup-banner">
        <span>Finish setting up your school — add your school&apos;s and principal&apos;s details. You&apos;ll add teachers and students from their own pages.</span>
        <Link to="/school-setup" className="btn-primary">Set up your school</Link>
      </div>
    );
  }
  // Nothing to explain once the school has people in it (roster imported, or they were added by hand).
  if (me.roster.status === "imported" || me.teacher_count + me.student_count > 0) return null;
  const r = me.roster;
  return (
    <div className={`${card} bg-gold-500/5 text-sm`} data-testid="roster-pending">
      <strong>Your roster ({r.teachers} teacher{r.teachers !== 1 ? "s" : ""}, {r.students} student{r.students !== 1 ? "s" : ""})</strong>{" "}
      {ROSTER_TEXT[r.status] || "is being processed"}. This was sent through a shared link, so an admin has to approve it first.
      You can also add your teachers and students yourself right now from the Students and Teachers pages.
    </div>
  );
}


// ------------------------------------------------------------------ overview

export function SchoolOverview({ token }) {
  const [a, setA] = useState(null);
  const [me] = useSchoolMe(token);
  useEffect(() => { schoolAnalytics(token).then(setA).catch((e) => toast.error(errorMessage(e))); }, [token]);
  if (!a) return <div className="text-forest-700">Loading…</div>;
  const max = Math.max(1, ...Object.values(a.distribution));
  return (
    <div className="space-y-6" data-testid="school-overview">
      <div><h1 className="font-display font-semibold text-2xl text-forest-700">{a.school}</h1>
        <p className="text-sm text-foreground/70">{a.role === "principal" ? "All classes" : "Your classes"}</p></div>
      <RosterNotice me={me} />
      {a.total_students === 0 && (
        <div className={`${card} bg-gold-500/5 text-sm`} data-testid="no-students-note">
          No students yet. Add them from the Students page, or ask our team to import your roster.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Students", a.total_students], ["Active", a.active_students], ["Average progress", `${a.average_percent}%`], ["Classes", a.by_class.length]].map(([l, v]) => (
          <div key={l} className={card}><div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className="mt-1 text-2xl font-display font-semibold text-forest-700">{v}</div></div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className={card}><h2 className={`${h2} mb-3`}>Progress distribution</h2>
          <div className="space-y-2">{Object.entries(a.distribution).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 text-sm"><span className="w-24 text-foreground/70">{k.replace("_", " ")}</span>
              <div className="flex-1 h-3 rounded bg-sand-300 overflow-hidden"><div className="h-full bg-forest-600" style={{ width: `${(v / max) * 100}%` }} /></div>
              <span className="w-8 text-right">{v}</span></div>))}</div></div>
        <div className={card}><h2 className={`${h2} mb-3`}>By class</h2>
          {a.by_class.length ? a.by_class.map((c) => (
            <div key={c.class} className="mb-3"><div className="flex justify-between text-sm"><span>{c.class} · {c.students} students</span><span>{c.average_percent}%</span></div><Bar percent={c.average_percent} /></div>))
            : <div className="text-sm text-foreground/60">No classes yet.</div>}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ progress (one app at a time)

const STATUS_LABEL = { not_started: "Not started", unavailable: "Couldn't load", restricted: "Not available" };

function ProgressCell({ r }) {
  if (r.status !== "ok") return <span className="text-xs text-foreground/60">{r.summary || STATUS_LABEL[r.status] || "—"}</span>;
  return (
    <div className="min-w-[10rem]">
      <div className="flex items-center gap-2"><div className="flex-1"><Bar percent={r.percent} /></div><span className="text-xs tabular-nums w-12 text-right">{r.percent}%</span></div>
      {r.summary && <div className="mt-1 text-xs text-foreground/60">{r.summary}</div>}
    </div>
  );
}

export function SchoolProgress({ token, apps: licensed }) {
  const [product, setProduct] = useState("");
  const [d, setD] = useState(null);
  const [cardsDone, setCardsDone] = useState(false);   // Poems: until the cards have answered, show their placeholder, not the plain table that would then jump
  const [cards, setCards] = useState(null);   // Poems: the teacher / student progress cards (null = not loaded, the plain table is shown instead)
  const [loading, setLoading] = useState(true);
  // Only the apps this account can open (the school's licence includes them) can be picked. The rest stay in the list,
  // greyed out. No list given (older callers): every app stays selectable, as before.
  const isOpen = (id) => !licensed || licensed.some((a) => a.id === id && a.has_access && a.available);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    schoolProgress(token, product || undefined)
      .then((r) => {
        if (!alive) return;
        if (!product && !isOpen(r.product)) {   // the first app is locked: start on the first one that is open instead
          const first = r.apps.find((a) => isOpen(a.product));
          if (first) { setProduct(first.product); return; }
        }
        setD(r); if (!product) setProduct(r.product);
      })
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, product]); // eslint-disable-line react-hooks/exhaustive-deps
  const principalView = d?.role === "principal";
  useEffect(() => {
    setCards(null);
    setCardsDone(false);
    if (product !== "poems" || !d) return undefined;
    let alive = true;
    poemsProgressCards(token, principalView).then((c) => alive && setCards(c)).catch(() => {}).finally(() => alive && setCardsDone(true));
    return () => { alive = false; };
  }, [token, product, principalView, !!d]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="text-forest-700">{loading ? "Loading…" : "Progress isn't available right now."}</div>;
  const sum = (x, label) => (
    <div className="text-sm text-foreground/70">{x.total} {label} · {x.started} started · average {x.average_percent}%</div>
  );
  return (
    <div className="space-y-6" data-testid="school-progress">
      <div className={`${card} flex flex-wrap items-center gap-3`}>
        <label htmlFor="progress-app" className="text-sm font-semibold text-forest-700">Application</label>
        <select id="progress-app" data-testid="progress-app" className={`${field} max-w-xs`} value={product} onChange={(e) => setProduct(e.target.value)}>
          {d.apps.map((a) => <option key={a.product} value={a.product} disabled={!isOpen(a.product)}>{a.name}{isOpen(a.product) ? "" : " (not included)"}</option>)}
        </select>
        {loading && <span className="text-xs text-foreground/60">Updating…</span>}
      </div>

      {product === "poems" && cards && cards.teachers.length > 0 && (
        <div className="space-y-8" data-testid="progress-cards">
          <TeacherProgressCards data={cards} />
          <StudentProgressTables data={cards} />
        </div>
      )}

      {product === "poems" && !cardsDone && <div className="space-y-4" data-testid="progress-cards-loading"><div className="h-7 w-56 rounded-lg bg-sand animate-pulse" /><div className="h-72 rounded-2xl bg-white animate-pulse" /></div>}

      {!(product === "poems" && !cardsDone) && !(product === "poems" && cards && cards.teachers.length > 0) && <div className={card} data-testid="progress-students">
        <div className="flex flex-wrap items-baseline justify-between gap-2"><h2 className={h2}>{d.learner_label || "Students"} · {d.app_name}</h2>{sum(d.student_summary, (d.learner_label || "students").toLowerCase())}</div>
        {d.students.length === 0 ? <p className="mt-3 text-sm text-foreground/70">{d.note || "No students yet."}</p> : (
          <div className="mt-3 overflow-x-auto"><table className="w-full">
            <thead><tr><th className={th}>{d.learner_label ? "Learner" : "Student"}</th><th className={th}>{d.learner_label ? "Age group" : "Class"}</th><th className={th}>Progress</th><th className={th}>Last active</th></tr></thead>
            <tbody>{d.students.map((r) => (
              <tr key={r.user_id}><td className={td}>{r.name}</td><td className={td}>{r.class_name ? `${r.class_name} ${r.section || ""}` : "—"}</td>
                <td className={td}><ProgressCell r={r} /></td><td className={td}>{when(r.last_active_at)}</td></tr>))}</tbody>
          </table></div>)}
      </div>}

      {d.role === "principal" && d.product !== "poems" && (
        <div className={card} data-testid="progress-teachers">
          <div className="flex flex-wrap items-baseline justify-between gap-2"><h2 className={h2}>Teachers · {d.app_name}</h2>{sum(d.teacher_summary, "teachers")}</div>
          {d.teachers.length === 0 ? <p className="mt-3 text-sm text-foreground/70">No teachers yet.</p> : (
            <div className="mt-3 overflow-x-auto"><table className="w-full">
              <thead><tr><th className={th}>Teacher</th><th className={th}>Classes</th><th className={th}>Progress</th><th className={th}>Last active</th></tr></thead>
              <tbody>{d.teachers.map((r) => (
                <tr key={r.user_id}><td className={td}>{r.name}</td><td className={td}>{(r.classes || []).map(classLabel).join(", ") || "—"}</td>
                  <td className={td}><ProgressCell r={r} /></td><td className={td}>{when(r.last_active_at)}</td></tr>))}</tbody>
            </table></div>)}
        </div>
      )}
    </div>
  );
}


// ------------------------------------------------------------------ students

function StudentDetail({ token, id, onBack, onRemoved }) {
  const [d, setD] = useState(null);
  const [creds, setCreds] = useState(null);
  const [editAge, setEditAge] = useState(null);
  const [me] = useSchoolMe(token);
  useEffect(() => { schoolStudent(token, id).then(setD).catch((e) => { toast.error(errorMessage(e)); onBack(); }); }, [token, id, onBack]);
  if (!d) return <div className="text-forest-700">Loading…</div>;
  const s = d.student;
  const reset = async () => {
    if (!window.confirm(`Reset ${s.name}'s password? The old one stops working.`)) return;
    try { const r = await schoolResetStudent(token, id); setCreds([{ name: s.name, username: r.username, class_name: s.class_name, section: s.section, password: r.password }]); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const saveAge = async () => {
    const age = parseInt(editAge, 10);
    if (!(age >= 2 && age <= 100)) return toast.error("Enter an age between 2 and 100");
    try { await schoolSetStudentAge(token, id, age); setEditAge(null); toast.success("Age updated"); setD(await schoolStudent(token, id)); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${s.name}? Their account stops working and their licence is freed.`)) return;
    try { await schoolRemoveStudent(token, id); toast.success(`${s.name} was removed`); onRemoved(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-4" data-testid="student-detail">
      <button type="button" onClick={onBack} className="text-sm text-forest-700 hover:underline inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />All students</button>
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className={h2}>{s.name}</h2>
          <span className="flex gap-2"><button type="button" className="btn-ghost" onClick={reset}><KeyRound className="h-4 w-4" /> Reset password</button>
            <button type="button" className="btn-ghost text-red-700" onClick={remove} data-testid="remove-student">Remove</button></span>
        </div>
        <div className="mt-1 text-sm text-foreground/70">Username {s.username} · Class {s.class_name} {s.section}{s.age ? ` · age ${s.age}` : ""} · last active {when(s.last_active_at)}</div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="age-access">
          <span>{s.access !== "full" ? <strong className="text-amber-800">{bandOf(me, s.access).label} apps: {bandOf(me, s.access).apps.join(", ")}</strong> : <strong className="text-forest-700">All apps in your licence</strong>}</span>
          <span className="text-foreground/60">· age {s.age || "not set"}</span>
          {editAge === null
            ? <button type="button" className="text-forest-700 underline" onClick={() => setEditAge(String(s.age || ""))} data-testid="age-edit">Change age</button>
            : <span className="flex items-center gap-1"><input type="number" min={2} max={100} className={`${field} w-20`} value={editAge} onChange={(e) => setEditAge(e.target.value)} data-testid="age-input" />
                <button type="button" className="btn-primary" onClick={saveAge} data-testid="age-save">Save</button>
                <button type="button" className="btn-ghost" onClick={() => setEditAge(null)}>Cancel</button></span>}
        </div>
        <div className="mt-4 font-semibold text-forest-700">{d.progress.reported ? `${d.progress.percent}% overall` : "No progress reported yet"}</div>
        <div className="mt-1"><Bar percent={d.progress.percent} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {d.progress.apps.map((p) => (
          <div key={p.product} className={card}>
            <div className="flex justify-between"><div className="font-semibold text-forest-700">{p.name}</div>{p.status === "ok" && <div className="text-sm font-semibold">{p.percent}%</div>}</div>
            {p.status === "ok" ? (<><div className="mt-2"><Bar percent={p.percent} /></div><div className="mt-2 text-sm text-foreground/70">{p.summary}</div></>)
              : <div className="mt-2 text-sm text-foreground/60">{p.status === "not_started" ? "Hasn't opened this app yet" : "Couldn't load right now"}</div>}
          </div>
        ))}
      </div>
      {creds && <CredentialsModal title="New password" rows={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

const DEFAULT_RULE = { bands: [{ id: "young", label: "3-7", apps: ["Quran Fluency", "Children Poems", "Read Qur'an", "Tajweed"] },
  { id: "junior", label: "8-13", apps: ["Quran Fluency", "Children Poems", "Read Qur'an", "Tajweed", "600 Qur'anic Stories", "Hifz with Hikmah"] },
  { id: "full", label: "14+", apps: null }] };
const ageRuleOf = (me) => (me && me.age_rule) || DEFAULT_RULE;
const bandOf = (me, id) => ageRuleOf(me).bands.find((b) => b.id === id) || DEFAULT_RULE.bands.find((b) => b.id === id) || { label: "", apps: [] };

function AddStudents({ token, role, remaining, ageRule, onDone, onClose }) {
  const [rows, setRows] = useState([{ name: "", class_name: "", section: "", age: "" }]);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (i, k) => (e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const importPaste = () => {
    const parsed = paste.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [name = "", class_name = "", section = "", age = ""] = l.split(/\t|,/).map((x) => x.trim());
      return { name, class_name, section, age };
    });
    setRows((r) => [...r.filter((x) => x.name), ...parsed]); setPaste("");
  };
  const submit = async () => {
    const students = rows.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), class_name: r.class_name.trim(), section: r.section.trim(), age: r.age ? parseInt(r.age, 10) : null }));
    if (!students.length) return toast.error("Add at least one student");
    if (students.some((s) => !s.class_name || !s.section)) return toast.error("Every student needs a class and section");
    if (students.some((s) => !s.age || s.age < 2)) return toast.error("Every student needs an age: it decides which apps they get");
    if (remaining != null && students.length > remaining) return toast.error(`You have ${remaining} licence${remaining === 1 ? "" : "s"} left, and this adds ${students.length}. Remove some rows or ask for more licences.`);
    setBusy(true);
    try { onDone((await schoolAddStudents(token, students)).students); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add students" onClose={onClose} wide>
      <p className="text-sm text-foreground/70">Each student gets a username and a temporary password to sign in with, and uses one licence. {role === "teacher" ? "You can only add students to your own classes." : ""}</p>
      <p className="mt-1 text-xs text-foreground/70" data-testid="age-rule-note">Age decides which apps a student gets: {ageRule.bands.map((b) => `${b.label} - ${b.apps ? b.apps.join(", ") : "every app in your licence"}`).join("; ")}.</p>
      {remaining != null && <p className="mt-1 text-sm font-semibold text-forest-700" data-testid="add-remaining">{remaining} licence{remaining === 1 ? "" : "s"} left</p>}
      <details className="mt-3 rounded-xl border border-sand-300 p-3"><summary className="cursor-pointer text-sm text-forest-700">Paste many at once</summary>
        <p className="mt-2 text-xs text-foreground/60">One per line: name, class, section, age (optional).</p>
        <textarea className={`${field} mt-2 h-24 font-mono`} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Aisha Khan, 3, A, 8" />
        <button type="button" className="btn-ghost mt-2" onClick={importPaste}>Add pasted students</button></details>
      <div className="mt-3 space-y-2" data-testid="add-student-rows">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1.6fr_.7fr_.7fr_.6fr] gap-2">
            <input className={field} placeholder="Name" value={r.name} onChange={upd(i, "name")} />
            <input className={field} placeholder="Class" value={r.class_name} onChange={upd(i, "class_name")} />
            <input className={field} placeholder="Section" value={r.section} onChange={upd(i, "section")} />
            <input className={field} type="number" min={2} required placeholder="Age *" value={r.age} onChange={upd(i, "age")} />
          </div>))}
      </div>
      <div className="mt-3 flex justify-between">
        <button type="button" className="btn-ghost" onClick={() => setRows((r) => [...r, { name: "", class_name: r[r.length - 1]?.class_name || "", section: r[r.length - 1]?.section || "", age: "" }])}><Plus className="h-4 w-4" /> Add row</button>
        <button type="button" data-testid="create-students" disabled={busy} className="btn-primary disabled:opacity-60" onClick={submit}>{busy ? "Creating…" : "Create accounts"}</button>
      </div>
    </Modal>
  );
}

export function Students({ token, role }) {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [tch, setTch] = useState("");          // principal: show one teacher's students ("" = all students)
  const [teachers, setTeachers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [creds, setCreds] = useState(null);
  const [me, reloadMe] = useSchoolMe(token);
  useEffect(() => { if (role === "principal") schoolTeachers(token).then((d) => setTeachers(d.teachers || [])).catch(() => {}); }, [token, role]);
  const load = useCallback(() => schoolStudents(token).then((d) => setList(d.students)).catch((e) => toast.error(errorMessage(e))), [token]);
  useEffect(() => { load(); }, [load]);
  const back = useCallback(() => setSel(null), []);
  const teacherClasses = useMemo(() => { const t = teachers.find((x) => x.user_id === tch); return t ? new Set(t.classes.map(classLabel)) : null; }, [teachers, tch]);
  const resetPw = async (st) => {
    if (!window.confirm(`Reset ${st.name}'s password? The old one stops working.`)) return;
    try { const r = await schoolResetStudent(token, st.user_id); setCreds([{ name: st.name, username: r.username, class_name: st.class_name, section: st.section, password: r.password }]); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const removeSt = async (st) => {
    if (!window.confirm(`Remove ${st.name}? Their account stops working and their licence is freed.`)) return;
    try { await schoolRemoveStudent(token, st.user_id); toast.success(`${st.name} was removed`); load(); reloadMe(); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (sel) return <StudentDetail token={token} id={sel} onBack={back} onRemoved={() => { setSel(null); load(); reloadMe(); }} />;
  if (!list) return <div className="text-forest-700">Loading…</div>;
  return (
    <div className="space-y-4">
      <RosterNotice me={me} />
      <StudentsList list={list} role={role} teachers={teachers} teacherClasses={teacherClasses} tch={tch} setTch={setTch} me={me}
        canAdd={canInvite(me)} addTitle={inviteBlockedReason(me)} onAdd={() => setAdding(true)}
        onOpen={(st) => { if (!st.record) setSel(st.user_id); }} onReset={resetPw} onRemove={removeSt}
        ageBadge={(st) => (!st.record && st.access !== "full" ? <span className="ml-1 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`child-badge-${st.user_id}`}>Ages {bandOf(me, st.access).label} apps</span> : null)} />
{adding && <AddStudents token={token} role={role} remaining={me && me.license && !me.license.unlimited ? me.license.remaining : null} ageRule={ageRuleOf(me)} onClose={() => setAdding(false)} onDone={(c) => { setAdding(false); setCreds(c); load(); reloadMe(); }} />}
      {creds && <CredentialsModal title="Student accounts created" rows={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

// ------------------------------------------------------------------ teachers

export function Teachers({ token }) {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", classes: "" });
  const [creds, setCreds] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [me, reloadMe] = useSchoolMe(token);
  const load = useCallback(() => schoolTeachers(token).then((d) => setList(d.teachers)).catch((e) => toast.error(errorMessage(e))), [token]);
  useEffect(() => { load(); }, [load]);
  const inviteLookup = useEmailLookup((em) => schoolInviteCheck(token, em), adding ? form.email : "");   // is there an account with this e-mail?
  const [unassigned, setUnassigned] = useState([]);   // classes nobody teaches: the "empty boxes" left when a teacher goes
  const [pick, setPick] = useState([]);                // empty boxes ticked in the Add teacher box
  const loadUnassigned = useCallback(() => schoolUnassignedClasses(token).then((d) => setUnassigned(d.classes || [])).catch(() => {}), [token]);
  useEffect(() => { loadUnassigned(); }, [loadUnassigned]);
  const classKey = (c) => `${c.class_name}|${c.section}`;
  const [invites, setInvites] = useState([]);   // invitations sent from here that are waiting for an answer (or were declined)
  const loadInvites = useCallback(() => schoolInvites(token).then((d) => setInvites(d.invites || [])).catch(() => {}), [token]);
  useEffect(() => { loadInvites(); const t = setInterval(loadInvites, 30000); return () => clearInterval(t); }, [loadInvites]);
  const dropInvite = async (i) => { try { await schoolCancelInvite(token, i.invite_id); loadInvites(); } catch (err) { toast.error(errorMessage(err)); } };

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await onboardAddTeacher(token, form.email.trim(), unassigned.filter((c) => pick.includes(classKey(c))).map((c) => ({ class_name: c.class_name, section: c.section })));
      if (r.status === "invited") toast.success(`Invitation sent to ${r.teacher.name}. They join as a teacher once they approve it.`);
      else toast.success(`${r.teacher.name} was added`);
      setAdding(false); setForm({ name: "", email: "", classes: "" }); setPick([]); load(); loadInvites(); loadUnassigned(); reloadMe();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const saveClasses = async (t) => {
    try { await schoolTeacherClasses(token, t.user_id, parseClasses(editText)); setEditing(null); load(); toast.success("Classes updated"); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const [emailFor, setEmailFor] = useState(null);   // the teacher whose sign-in e-mail is being changed
  const [newEmail, setNewEmail] = useState("");
  const [confirmFor, setConfirmFor] = useState(null);   // { kind: "reset" | "remove", teacher }
  const [busy, setBusy] = useState(false);
  const emailLookup = useEmailLookup((em) => (emailFor ? schoolTeacherEmailCheck(token, emailFor.user_id, em) : Promise.resolve(null)), emailFor ? newEmail : "");
  const openEmail = (t) => { setEmailFor(t); setNewEmail(""); };
  const saveEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await schoolSetTeacherEmail(token, emailFor.user_id, newEmail.trim()); toast.success(r.message || "E-mail changed"); setEmailFor(null); load(); reloadMe(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const runConfirmed = async () => {
    const { kind, teacher: t } = confirmFor;
    setBusy(true);
    try {
      if (kind === "reset") { const r = await schoolResetTeacher(token, t.user_id); setCreds([{ name: t.name, email: t.email, password: r.password }]); }
      else { await schoolRemoveTeacher(token, t.user_id); toast.success(`${t.name} was removed. Their classes are kept as empty boxes.`); load(); loadUnassigned(); reloadMe(); }
      setConfirmFor(null);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  if (!list) return <div className="text-forest-700">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="teachers-tab">
      <RosterNotice me={me} />
      <TeachersList list={list} unassigned={unassigned} invites={invites} canAdd={canInvite(me)} addTitle={inviteBlockedReason(me)}
        onAdd={() => { setPick([]); setAdding(true); }} onAssign={(c) => { setPick([classKey(c)]); setAdding(true); }}
        onEmail={openEmail} onReset={(t) => setConfirmFor({ kind: "reset", teacher: t })} onRemove={(t) => setConfirmFor({ kind: "remove", teacher: t })}
        editing={editing} editText={editText} setEditText={setEditText} startEdit={(t) => { setEditing(t.user_id); setEditText((t.classes || []).map(classLabel).join(", ")); }}
        cancelEdit={() => setEditing(null)} saveClasses={saveClasses}
        onDismissClass={(c) => schoolDismissClass(token, { class_name: c.class_name, section: c.section }).then(loadUnassigned).catch((e) => toast.error(errorMessage(e)))}
        onDropInvite={dropInvite} />
      {adding && (
        <Modal title="Add a teacher" onClose={() => setAdding(false)}>
          <form onSubmit={add} className="space-y-3">
            <input required type="email" className={field} placeholder="teacher@school.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email" />
            <EmailLookupNote info={inviteLookup.info} loading={inviteLookup.loading} />
            {!inviteLookup.info && !inviteLookup.loading && <p className="text-xs text-foreground/60">Use the e-mail they registered on HikmahVerses with. They get an invitation and become a teacher once they approve it; each teacher then uses one of your places.</p>}
            {unassigned.length > 0 && (
              <fieldset className="rounded-xl border border-sand-300 p-3" data-testid="pick-classes">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-foreground/60">Give them these classes (optional)</legend>
                <div className="flex flex-wrap gap-2">
                  {unassigned.map((c) => (
                    <label key={classKey(c)} className={`cursor-pointer select-none rounded-full border px-3 py-1.5 text-sm ${pick.includes(classKey(c)) ? "border-forest-700 bg-forest-700 text-white" : "border-sand-300 bg-white text-forest-700 hover:border-forest-600"}`}>
                      <input type="checkbox" className="sr-only" checked={pick.includes(classKey(c))} onChange={() => setPick((p) => (p.includes(classKey(c)) ? p.filter((k) => k !== classKey(c)) : [...p, classKey(c)]))} data-testid={`pick-${classKey(c)}`} />
                      {classLabel(c)} <span className="opacity-70">· {c.students}</span>
                    </label>))}
                </div>
              </fieldset>)}
            <button type="submit" className="btn-primary w-full disabled:opacity-50" disabled={!form.email.trim() || inviteLookup.blocked || inviteLookup.loading || (inviteLookup.info && inviteLookup.info.state !== "found")} data-testid="create-teacher">Send invitation</button>
          </form>
        </Modal>)}
      {emailFor && (
        <Modal title="Change sign-in e-mail" onClose={() => setEmailFor(null)}>
          <form onSubmit={saveEmail} className="space-y-4" data-testid="change-email-modal">
            <div className="rounded-xl bg-sand-100 px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Teacher</div>
              <div className="font-semibold text-forest-700">{emailFor.name}</div>
              <div className="text-sm text-foreground/70 break-all">{emailFor.email}</div>
            </div>
            <label className="block text-sm font-semibold text-forest-700">New e-mail
              <input required autoFocus type="email" className={`${field} mt-1 font-normal`} placeholder="teacher@school.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-teacher-email" /></label>
            <EmailLookupNote info={emailLookup.info} loading={emailLookup.loading} />
            {!emailLookup.info && !emailLookup.loading && emailFor.self_registered && <p className="text-xs text-foreground/60">{emailFor.name} created this account themselves, so their login isn&apos;t edited. Enter the e-mail of the person who should take over: they must already have a HikmahVerses account, and they get this teacher&apos;s classes and students. {emailFor.name}&apos;s place in the school is then closed.</p>}
            {!emailLookup.info && !emailLookup.loading && !emailFor.self_registered && <p className="text-xs text-foreground/60">If this person already has a HikmahVerses account, they take over this teacher&apos;s classes and students and use that account from now on. Otherwise this just updates the e-mail the teacher signs in with.</p>}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setEmailFor(null)}>Cancel</button>
              <button type="submit" className="btn-primary disabled:opacity-50" disabled={busy || !newEmail.trim() || emailLookup.blocked} data-testid="save-teacher-email">{busy ? "Saving…" : "Save e-mail"}</button>
            </div>
          </form>
        </Modal>)}
      {confirmFor && (
        <Modal title={confirmFor.kind === "reset" ? "Reset password" : "Remove teacher"} onClose={() => setConfirmFor(null)}>
          <div className="space-y-4" data-testid="teacher-confirm-modal">
            <p className="text-sm text-foreground/80">{confirmFor.kind === "reset"
              ? <>Give <strong>{confirmFor.teacher.name}</strong> a new temporary password? The old one stops working and they choose their own at next sign-in.</>
              : <>Remove <strong>{confirmFor.teacher.name}</strong> from the school? Their licence place is freed and they lose access. Their classes{confirmFor.teacher.classes && confirmFor.teacher.classes.length ? <> (<strong>{confirmFor.teacher.classes.map(classLabel).join(", ")}</strong>)</> : null} stay in the school as empty boxes, so you can give them to the next teacher.</>}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setConfirmFor(null)}>Cancel</button>
              <button type="button" className={confirmFor.kind === "remove" ? "btn-primary !bg-red-700 hover:!bg-red-800 disabled:opacity-50" : "btn-primary disabled:opacity-50"} disabled={busy} onClick={runConfirmed} data-testid="teacher-confirm">
                {busy ? "Please wait…" : confirmFor.kind === "reset" ? "Reset password" : "Remove teacher"}</button>
            </div>
          </div>
        </Modal>)}
      {creds && <CredentialsModal title="Teacher account" rows={creds} onClose={() => setCreds(null)} note="This is a temporary password. When the teacher signs in with it, they are asked to set their own new password before they can continue." />}
    </div>
  );
}
